import { evaluate } from './predicates.js';
import { identityNodeId } from './state.js';
/** Outcome ids a package may define to author a gate's consequence. */
export const GATE_OUTCOME_IDS = {
    channel_access: 'gate_channel_access',
    corporate_cred: 'gate_overstepping',
    pip: 'gate_pip',
};
/** Applied when a package authors no overstepping outcome. Overstepping always costs a strike. */
const SYNTHESISED_OVERSTEP = { kind: 'gated', pipDelta: 1 };
/** Applied when a package authors no outcome for a structural refusal. */
const SYNTHESISED_REFUSAL = { kind: 'gated' };
const pass = (gate) => ({
    gate,
    passed: true,
    reason: null,
    outcomeId: null,
});
/** The authored outcome for a gate, or the synthesised fallback. */
function outcomeFor(loaded, authoredId, fallbackId, fallback) {
    if (authoredId !== undefined) {
        const authored = loaded.outcomes[authoredId];
        if (authored !== undefined)
            return { id: authoredId, outcome: authored };
    }
    const conventional = loaded.outcomes[fallbackId];
    if (conventional !== undefined)
        return { id: fallbackId, outcome: conventional };
    return { id: fallbackId, outcome: fallback };
}
function fail(gate, reason, resolved, voicePenalty = null) {
    return {
        result: { gate, passed: false, reason, outcomeId: resolved.id },
        outcomeId: resolved.id,
        outcome: resolved.outcome,
        voicePenalty,
    };
}
/** The credibility and lockout cost of a voice-mask violation under this profile. */
function voicePenaltyFor(state, puppet) {
    const nodeId = puppet.nodeId;
    switch (state.settings.voiceMaskPenalty) {
        case 'warning':
            return { nodeId, credibilityDelta: 0, lockedUntilDay: null };
        case 'credibility':
            return {
                nodeId,
                credibilityDelta: -puppet.credibility.violationPenalty,
                lockedUntilDay: null,
            };
        case 'lockout':
            return {
                nodeId,
                credibilityDelta: -puppet.credibility.violationPenalty,
                lockedUntilDay: state.clock.day + 1,
            };
    }
}
/**
 * Runs the gates for a player send.
 *
 * Propagation hops are not gated: the proposal is travelling between nodes on
 * its own, and none of these four questions is being asked of the player.
 */
export function runGates(loaded, state, targetNodeId, channelId, context) {
    const results = [];
    // 1. Channel access.
    const channel = loaded.channels[channelId];
    const node = loaded.nodes[targetNodeId];
    const channelState = state.channels[channelId];
    const accessRefusal = outcomeFor(loaded, undefined, GATE_OUTCOME_IDS.channel_access, SYNTHESISED_REFUSAL);
    if (channel === undefined || channelState === undefined || !channelState.unlocked) {
        return { results, failure: fail('channel_access', 'channel_locked', accessRefusal) };
    }
    if (node === undefined) {
        return { results, failure: fail('channel_access', 'unknown_target', accessRefusal) };
    }
    if (node.addressable === false) {
        return { results, failure: fail('channel_access', 'node_not_addressable', accessRefusal) };
    }
    if (!channel.members.includes(targetNodeId)) {
        return { results, failure: fail('channel_access', 'target_not_in_channel', accessRefusal) };
    }
    results.push(pass('channel_access'));
    // 2. Corporate Cred. Speaking above your level is overstepping, and HR hears
    //    about it — the message is never judged on its merits.
    if (state.cred < node.credThreshold) {
        const resolved = outcomeFor(loaded, undefined, GATE_OUTCOME_IDS.corporate_cred, SYNTHESISED_OVERSTEP);
        return {
            results,
            failure: fail('corporate_cred', 'cred_below_threshold', resolved),
        };
    }
    results.push(pass('corporate_cred'));
    // 3. PIP restrictions.
    const pipRefusal = outcomeFor(loaded, undefined, GATE_OUTCOME_IDS.pip, SYNTHESISED_REFUSAL);
    if (state.pip.composerLockTicks > 0) {
        return { results, failure: fail('pip', 'composer_locked', pipRefusal) };
    }
    const requiredTone = state.pip.openerToneRequired;
    if (requiredTone !== null) {
        const opener = context.selection.find((slot) => loaded.particles[slot.particleId]?.slotRole === 'opener');
        const particle = opener === undefined ? undefined : loaded.particles[opener.particleId];
        const tone = particle?.tags.tone;
        const tones = tone === undefined ? [] : Array.isArray(tone) ? tone : [tone];
        if (!tones.includes(requiredTone)) {
            return { results, failure: fail('pip', 'opener_tone_restricted', pipRefusal) };
        }
    }
    results.push(pass('pip'));
    // 4. Voice mask. Only asked when the player is wearing someone else's voice.
    //    Read from the context rather than the state, because a synchronised
    //    pitch runs this gate once per staged draft, each in its own voice, while
    //    the state's active identity is whichever one happens to be worn.
    const puppetNodeId = identityNodeId(context.identity);
    if (puppetNodeId !== null) {
        const puppet = loaded.puppets[puppetNodeId];
        if (puppet === undefined) {
            return { results, failure: fail('voice_mask', 'unknown_puppet', pipRefusal) };
        }
        const requires = puppet.voice.requires;
        const forbids = puppet.voice.forbids;
        const brokenBy = requires !== undefined && !evaluate(requires, context)
            ? 'voice_requires_unmet'
            : forbids !== undefined && evaluate(forbids, context)
                ? 'voice_forbids_matched'
                : null;
        if (brokenBy !== null) {
            const resolved = outcomeFor(loaded, puppet.voice.gateOutcome, GATE_OUTCOME_IDS.pip, SYNTHESISED_REFUSAL);
            return {
                results,
                failure: fail('voice_mask', brokenBy, resolved, voicePenaltyFor(state, puppet)),
            };
        }
    }
    results.push(pass('voice_mask'));
    return { results, failure: null };
}
//# sourceMappingURL=gates.js.map